import ReducerRegistry from "seneca-common/utils/state/reducer-registry";

import {
  PATH_TO_CONTENT_STATS_STATE,
  PATH_TO_COURSE_STATS_STATE,
  PATH_TO_SECTION_STATS_STATE,
  PATH_TO_USER_STATS_STATE
} from "./consts";
import contentStatsReducer from "./reducers/contentStats";
import courseStatsReducer from "./reducers/courseStats";
import sectionStatsReducer from "./reducers/sectionStats";
import userStatsReducer from "./reducers/userStats";
import * as contentStatsSelectors from "./selectors/state/contentStats/stats";
import * as sectionStatsSelectors from "./selectors/state/sectionStats/stats";
import * as sectionStatSelectors from "./selectors/state/sectionStats/stats/stat";

const reducerRegistry = ReducerRegistry.getInstance();
reducerRegistry.initialise(PATH_TO_USER_STATS_STATE, builder => {
  builder.registerMultiple({
    [PATH_TO_USER_STATS_STATE]: userStatsReducer,
    [PATH_TO_COURSE_STATS_STATE]: courseStatsReducer,
    [PATH_TO_SECTION_STATS_STATE]: sectionStatsReducer,
    [PATH_TO_CONTENT_STATS_STATE]: contentStatsReducer
  });
});

// using 'require' to make sure that analytics code executes AFTER reducer is registered
require("./analytics");

// models
//User
export { UserStat, RecentlyStudiedCourse } from "./models/state/userStats";
export type {
  UserStatType,
  UserStatServerType,
  RecentlyStudiedCourseType
} from "./models/state/userStats";

//Course
export { CourseStat } from "./models/state/courseStats";
export type { CourseStatType } from "./models/state/courseStats";

//Section
export type {
  SectionStatType,
  SectionStats
} from "./models/state/sectionStats";
export {
  default as SectionStatSlice,
  SectionStat
} from "./models/state/sectionStats";

//Content
export { ContentStat } from "./models/state/contentStats";
export type { ContentStatType } from "./models/state/contentStats";

//Events
export { default as ModuleStudiedEvent } from "./models/ModuleStudiedEvent";
export type { ModuleStudiedEventType } from "./models/ModuleStudiedEvent";
export { default as ContentModuleMeta } from "./models/ContentModuleMeta";
export type { ContentModuleMetaType } from "./models/ContentModuleMeta";

// reducers
export {
  userStatsReducer,
  courseStatsReducer,
  sectionStatsReducer,
  contentStatsReducer
};

// selectors
export { sectionStatsSelectors, contentStatsSelectors, sectionStatSelectors };

export {
  // users
  getUserTotalStudyTime,
  getUserSessionsCompleted,
  getUserLastStudied,
  getUserSessionModulesStudied,
  getUserSessionModulesCorrect,
  getUserRecentlyStudiedCourses,
  getUserRecentlyStudiedCourseIds,
  userStatsFetching,
  userStatsFetchSucceeded,
  userStatsFetchErrored,
  userStatsFetchFinished,
  userStatsNeedsFetching, // content
  getContentTimesStudied,
  getContentLastStudied,
  getContentLastScore,
  getLastContentModuleId,
  getContentStat,
  getContentStats,
  hasContentStat,
  lastTryForContentWasIncorrect,
  allContentStatsForCourseFetching,
  allContentStatsForCourseFetchSucceeded,
  allContentStatsForCourseFetchErrored,
  allContentStatsForCourseFetchFinished,
  allContentStatsForCourseNeedsFetching, // sections
  hasSectionStat,
  getSectionStat,
  getSectionTimesStudied,
  getSectionTotalStudyTime,
  getSectionBestScore,
  getSectionLastScore,
  getSectionAverageScore,
  getSectionSpacing,
  getSectionLastStudied,
  getSectionModuleIdsIncorrectLast,
  getNumberOfSectionModuleIdsIncorrectLast,
  getSectionStats,
  getSectionsStats as getStatsForSections,
  getStudiedSectionIds,
  allSectionStatsForCourseFetchErrored,
  allSectionStatsForCourseFetchFinished,
  allSectionStatsForCourseFetchSucceeded,
  allSectionStatsForCourseNeedsFetching, // courses
  getCourseTotalStudyTime,
  getCourseScore,
  getCourseSessionsCompleted,
  getNumberOfModulesStudiedForCourse,
  getCourseProficiency,
  getCourseLastStudied,
  courseStatFetchSucceeded,
  courseStatFetchErrored,
  courseStatFetchFinished,
  courseStatNeedsFetching,
  allCourseStatsFetchErrored,
  allCourseStatsFetchFinished,
  allCourseStatsNeedsFetching
} from "./selectors/state";

export { selectStudiedSectionIdsFromCourse } from "./selectors/state/selectStudiedSectionIdsFromCourse";

// actions
export {
  receiveReconciledUserStudyStats,
  receiveReconciledCourseStudyStats,
  receiveReconciledSectionStudyStats,
  receiveReconciledContentStudyStats,
  receiveAndResetContentStudyStats,
  receiveUserStudyStats,
  receiveCourseStudyStats,
  receiveSectionStudyStats,
  receiveContentStudyStats,
  receiveAllUserCoursesStats
} from "./actions";
export {
  fetchUserStatActionFactory,
  fetchCourseStatActionFactory,
  fetchAllCourseStatsActionFactory,
  fetchSectionStatActionFactory,
  fetchContentStatsActionFactory
} from "./actions/fetchStates";

// logic
export {
  applyCompleteSessionEventToUserStat,
  transformServerUserStat
} from "./reducers/userStats/state/stat";
export {
  applyCompleteSessionEventToCourseStat,
  transformServerCourseStat
} from "./reducers/courseStats/state/stats";
export {
  applyCompleteSessionEventToSectionStat,
  transformServerSectionStat
} from "./reducers/sectionStats/state/stats";
export {
  applyCompleteModuleEventToContentStat,
  transformServerContentStat
} from "./reducers/contentStats/state/stats";
